// ---------------------------------------------------------------------------
// consent — cookie consent banner (orestbida vanilla-cookieconsent v2).
//
// Ported from the legacy js/consent.js. Presents a "necessary + analytics"
// opt-in banner whose colours are themed via the --cc-* CSS variables defined
// in sass/foundation/_body.scss (the vendor stylesheet is inlined by base.scss).
//
// Analytics gating: Google Tag Manager is only injected once the visitor
// accepts the `analytics` category. The GTM container id is read from
// `window.googleTagManagerMainTag`, which base.html sets from settings.gtmId.
// When no id is configured the accept handler simply skips the GTM load, so
// nothing throws on sites without analytics.
//
// The library attaches `window.initCookieConsent` as a side effect of import.
// ---------------------------------------------------------------------------

import 'vanilla-cookieconsent';

const PRIVACY_URL = 'https://offworld.live/privacy-policy';

// Push GTM consent-mode signals and lazily inject gtm.js once analytics is
// allowed. No-ops when GTM isn't configured on the page.
const loadGoogleTagManager = (allowAnalytics: boolean): void => {
	const w = window as typeof window & {
		gtag?: (...args: unknown[]) => void;
		googleTagManagerMainTag?: string;
		__gtmLoaded?: boolean;
	};

	if (typeof w.gtag === 'function') {
		const value = allowAnalytics ? 'granted' : 'denied';
		w.gtag('consent', 'update', {
			ad_storage: value,
			analytics_storage: value,
			personalization_storage: value,
			functionality_storage: value,
			security_storage: 'granted',
		});
	}

	const tag = w.googleTagManagerMainTag;
	if (!allowAnalytics || !tag || w.__gtmLoaded) return;
	w.__gtmLoaded = true;

	const dl = (window as typeof window & { dataLayer?: unknown[] }).dataLayer;
	if (dl) dl.push({ 'gtm.start': Date.now(), event: 'gtm.js' });

	const script = document.createElement('script');
	script.async = true;
	script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(tag)}`;
	document.head.appendChild(script);
};

export const initConsent = (): void => {
	if (typeof window.initCookieConsent !== 'function') return;

	const cc = window.initCookieConsent();

	cc.run({
		current_lang: 'en',
		autoclear_cookies: true,
		page_scripts: true,
		gui_options: {
			consent_modal: { layout: 'bar', position: 'middle center', transition: 'slide' },
			settings_modal: { layout: 'box', position: 'left', transition: 'slide' },
		},

		onAccept: (cookie) => {
			const categories = (cookie && cookie.categories) || [];
			loadGoogleTagManager(categories.includes('analytics'));
		},

		onChange: (cookie) => {
			const categories = (cookie && cookie.categories) || [];
			loadGoogleTagManager(categories.includes('analytics'));
		},

		languages: {
			en: {
				consent_modal: {
					title: 'We use cookies!',
					description:
						'This website uses essential cookies to ensure its proper operation and analytics cookies to understand how you interact with it. The latter are set only after consent. <button type="button" data-cc="c-settings" class="cc-link">Let me choose</button>',
					primary_btn: { text: 'Accept all', role: 'accept_all' },
					secondary_btn: { text: 'Reject all', role: 'accept_necessary' },
				},
				settings_modal: {
					title: 'Cookie preferences',
					save_settings_btn: 'Save settings',
					accept_all_btn: 'Accept all',
					reject_all_btn: 'Reject all',
					close_btn_label: 'Close',
					blocks: [
						{
							title: 'Cookie usage',
							description: `We use cookies to ensure the basic functionalities of the website and to enhance your online experience. You can opt in or out of each category at any time. For more details please read our <a href="${PRIVACY_URL}" class="cc-link">privacy policy</a>.`,
						},
						{
							title: 'Strictly necessary cookies',
							description:
								'These cookies are essential for the proper functioning of the website. Without them the website would not work.',
							toggle: { value: 'necessary', enabled: true, readonly: true },
						},
						{
							title: 'Performance and analytics cookies',
							description:
								'These cookies allow us to measure traffic and understand how visitors interact with the site (via Google Tag Manager). They are only set after you consent.',
							toggle: { value: 'analytics', enabled: false, readonly: false },
						},
					],
				},
			},
		},
	});
};
